import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Paper,
  Box,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  CheckCircle as CheckCircleIcon,
  History as HistoryIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { getUserCreditsApi, UserCreditsResponse, UserCreditsPackage } from '../../services/paymentApi';

// Default data structure with 0 values
const defaultCreditsData: UserCreditsResponse = {
  success: false,
  credits: {
    total: 0,
    used: 0,
    remaining: 0,
  },
  tierCredits: {},
  packages: [],
  hasActivePackage: false,
  hasRemainingCredits: false,
};

const Credits: React.FC = () => {
  const [data, setData] = useState<UserCreditsResponse>(defaultCreditsData);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCredits = async () => {
      try {
        setLoading(true);
        const res = await getUserCreditsApi();
        setData(res);
      } catch (err: any) {
        // On error, set data to default (0 values) instead of showing error
        setData(defaultCreditsData);
        // Optionally log the error for debugging but don't show it to user
        console.error('Failed to fetch user credits:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCredits();
  }, []);

  const renderPackageType = (pkg: UserCreditsPackage) => {
    if (pkg.tier === 'custom') return 'Custom Package';
    if (pkg.tier === '4-5') return 'Tier 4 & 5 Annual Package';
    return `Tier ${pkg.tier} Annual Package`;
  };

  const renderPackageUsageSummary = (pkg: UserCreditsPackage) => {
    if (pkg.tier === 'custom') {
      return 'Flexible custom mix of Tier 1–5 PRs for one year. Credits are consumed each time you publish a PR using this custom package.';
    }
    if (pkg.tier === '4-5') {
      return '20 PR credits valid on Tier 4–5 publications for one year. Each distributed PR uses 1 credit.';
    }
    return `20 PR credits for Tier ${pkg.tier} publications, valid for one year. Each distributed PR uses 1 credit.`;
  };

  const renderTierBreakdown = (pkg: UserCreditsPackage) => {
    if (!pkg.tierPRCounts) return null;
    const entries = Object.entries(pkg.tierPRCounts).filter(([, count]) => count > 0);
    if (!entries.length) return null;
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
          Tier-wise credits:
        </Typography>
        {entries.map(([tier, count]) => (
          <Typography key={tier} variant="caption" sx={{ color: '#666', display: 'block' }}>
            Tier {tier}: {count} PR{count !== 1 ? 's' : ''}
          </Typography>
        ))}
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: '#0927eb',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <CreditCardIcon sx={{ fontSize: 32 }} />
          PR Credits & Packages
        </Typography>
        <Typography variant="body1" sx={{ color: '#666' }}>
          View your purchased PR packages, remaining credits, and tier-wise breakdown.
        </Typography>
      </Box>

      {loading && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
        </Box>
      )}

      {/* Always show UI, even if there was an error - data will have 0 values */}
      <>
          {/* Summary cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0927eb' }}>
                        {data.credits.total}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        Total Credits Purchased
                      </Typography>
                    </Box>
                    <BarChartIcon sx={{ fontSize: 40, color: '#0927eb', opacity: 0.3 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
                        {data.credits.used}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        Credits Used
                      </Typography>
                    </Box>
                    <HistoryIcon sx={{ fontSize: 40, color: '#4CAF50', opacity: 0.3 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF9800' }}>
                        {data.credits.remaining}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        Remaining Credits
                      </Typography>
                    </Box>
                    <CheckCircleIcon sx={{ fontSize: 40, color: '#FF9800', opacity: 0.3 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tier-Specific Credits Breakdown */}
          {data.tierCredits && Object.keys(data.tierCredits).filter(tier => (data.tierCredits?.[tier] || 0) > 0).length > 0 && (
            <Paper sx={{ p: 3, borderRadius: 2, mb: 4, backgroundColor: '#E3F2FD', border: '2px solid #09273b' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#09273b', fontSize: '1.25rem' }}>
                Available Credits by Tier
              </Typography>
              <Typography variant="body2" sx={{ color: '#4A5568', mb: 2, fontSize: '0.9rem' }}>
                These credits are only deducted when you use a <strong>matching tier package</strong>. Standalone payments and different tier packages do not use these credits.
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(data.tierCredits)
                  .filter(([_, count]) => count > 0)
                  .map(([tier, count]) => (
                    <Grid item xs={6} sm={4} md={3} key={tier}>
                      <Paper 
                        elevation={2} 
                        sx={{ 
                          p: 2, 
                          backgroundColor: '#fff', 
                          borderRadius: 2, 
                          textAlign: 'center',
                          border: '2px solid #ff5200',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 4
                          }
                        }}
                      >
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#09273b', mb: 0.5 }}>
                          {count}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
                          {tier === 'custom' ? 'Custom Package' : tier === '4-5' ? 'Tier 4-5' : `Tier ${tier}`} Credits
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                          {tier === 'custom' 
                            ? 'For custom packages only' 
                            : tier === '4-5'
                            ? 'For Tier 4-5 packages only'
                            : `For Tier ${tier} packages only`}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
              </Grid>
              <Alert severity="info" sx={{ mt: 3, backgroundColor: '#BBDEFB', border: '1px solid #1976D2' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: '#09273b' }}>
                  ⚠️ Important: Credits are tier-specific and only used for matching packages
                </Typography>
                <Box component="ul" sx={{ margin: 0, paddingLeft: 2, color: '#4A5568' }}>
                  <li><Typography variant="body2" sx={{ fontSize: '0.9rem' }}>Tier 2 credits can <strong>only</strong> be used for Tier 2 packages</Typography></li>
                  <li><Typography variant="body2" sx={{ fontSize: '0.9rem' }}>Tier 1 credits can <strong>only</strong> be used for Tier 1 packages</Typography></li>
                  <li><Typography variant="body2" sx={{ fontSize: '0.9rem' }}>Custom package credits are used according to the tier breakdown you selected</Typography></li>
                  <li><Typography variant="body2" sx={{ fontSize: '0.9rem' }}><strong>Standalone plans always require payment</strong> and <strong>never use credits</strong></Typography></li>
                  <li><Typography variant="body2" sx={{ fontSize: '0.9rem' }}>Using a different tier package than your credits requires payment</Typography></li>
                </Box>
              </Alert>
            </Paper>
          )}

          {/* How to use your credits */}
          <Paper sx={{ p: 3, borderRadius: 2, mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              How to use your PR credits
            </Typography>
            <Typography variant="body2" sx={{ color: '#555', mb: 1 }}>
              Credits are automatically applied when you select a <strong>matching tier</strong> PR package in the Distribution flow. Each time you publish a press release using a package, 1 credit is consumed from the matching tier.
            </Typography>
            <Typography variant="body2" sx={{ color: '#555', mb: 1 }}>
              To use your credits:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2, color: '#555' }}>
              <li>
                <Typography variant="body2">
                  Go to the Distribution page and select your geography, industry, and tiers.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  When choosing pricing, pick a PR package that <strong>matches your available tier credits</strong> (Tier 1–3, Tier 4–5, or Custom).
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Standalone plans always require payment</strong> and never use credits.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  If you select a different tier package than what you have credits for, you'll be shown the checkout page to pay.
                </Typography>
              </li>
            </Box>
            <Alert severity="warning" sx={{ backgroundColor: '#FFF3E0' }}>
              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                <strong>Example:</strong> If you have Tier 2 credits but select a Tier 1 package, you'll need to pay. Credits only work for matching tier packages.
              </Typography>
            </Alert>
          </Paper>

          {/* Packages table */}
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Package History
            </Typography>
            {data.packages.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#666' }}>
                No PR packages found. Purchase a package from the Distribution flow to start using PR credits.
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Package Type</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Total Credits</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Purchased On</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.packages.map((pkg, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {renderPackageType(pkg)}
                          </Typography>
                          {pkg.tier === 'custom' && (
                            <Chip
                              label="Custom"
                              size="small"
                              sx={{ mt: 0.5, fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#ff9800', color: '#fff' }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {pkg.credits} PR{pkg.credits !== 1 ? 's' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(pkg.purchasedAt).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {renderPackageUsageSummary(pkg)}
                          </Typography>
                          {renderTierBreakdown(pkg)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
      </>
    </Container>
  );
};

export default Credits;


